/**
 * Cache Service
 * Singleton service quản lý cache trong LocalStorage với TTL support
 */

import { CachedData, CacheStats, CacheResult } from './types';
import { GlobalCacheSettings } from './CacheConfig';

/**
 * Cache Service Class
 * Quản lý việc lưu trữ và truy xuất dữ liệu cache với automatic expiration
 */
class CacheServiceClass {
  private prefix: string = GlobalCacheSettings.prefix;
  private stats: CacheStats = { hits: 0, misses: 0, size: 0, keys: [] };
  private cleanupTimer: NodeJS.Timeout | null = null;
  
  constructor() {
    this.initCleanup();
    this.logDebug('CacheService initialized');
  }
  
  /**
   * Lấy dữ liệu từ cache
   * Tự động kiểm tra expiration và return null nếu đã hết hạn
   * 
   * @param key - Cache key (không bao gồm prefix)
   * @returns CacheResult với data và metadata
   */
  get<T>(key: string): CacheResult<T> {
    const fullKey = this.getFullKey(key);
    
    try {
      const item = localStorage.getItem(fullKey);
      
      if (!item) {
        this.stats.misses++;
        this.logDebug(`Cache miss: ${key}`);
        return { data: null, cached: false };
      }
      
      const cachedData: CachedData<T> = JSON.parse(item);
      const now = Date.now();
      
      // Kiểm tra expiration
      if (now > cachedData.expiresAt) {
        this.logDebug(`Cache expired: ${key}`);
        this.remove(key);
        this.stats.misses++;
        return { data: null, cached: false };
      }
      
      this.stats.hits++;
      this.logDebug(`Cache hit: ${key}`, cachedData.data);
      
      return {
        data: cachedData.data,
        cached: true,
        timestamp: cachedData.timestamp,
      };
    } catch (error) {
      console.error(`Error reading cache for key: ${key}`, error);
      this.remove(key); // Xóa cache lỗi
      this.stats.misses++;
      return { data: null, cached: false };
    }
  }
  
  /**
   * Lưu dữ liệu vào cache với TTL
   * Tự động cleanup nếu storage đầy
   * 
   * @param key - Cache key (không bao gồm prefix)
   * @param data - Dữ liệu cần cache
   * @param ttl - Time-to-live tính bằng milliseconds
   * @returns true nếu lưu thành công
   */
  set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): boolean {
    const fullKey = this.getFullKey(key);
    const now = Date.now();
    
    const cachedData: CachedData<T> = {
      data,
      timestamp: now,
      expiresAt: ttl === Infinity ? Infinity : now + ttl,
      version: GlobalCacheSettings.version,
    };
    
    try {
      const serialized = JSON.stringify(cachedData);
      
      // Kiểm tra size trước khi lưu
      if (this.getStorageSize() + serialized.length > GlobalCacheSettings.maxSize) {
        this.logDebug('Storage approaching limit, cleaning old entries');
        this.cleanOldEntries();
      }
      
      localStorage.setItem(fullKey, serialized);
      this.updateStats();
      this.logDebug(`Cache set: ${key}`, data);
      
      return true;
    } catch (error) {
      console.error(`Error saving cache for key: ${key}`, error);
      
      // Nếu lỗi do storage full, cleanup và retry
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.logDebug('QuotaExceededError, attempting cleanup and retry');
        this.cleanOldEntries();
        
        try {
          const serialized = JSON.stringify(cachedData);
          localStorage.setItem(fullKey, serialized);
          this.updateStats();
          return true;
        } catch (retryError) {
          console.error('Retry failed after cleanup:', retryError);
        }
      }
      
      return false;
    }
  }
  
  /**
   * Xóa một cache entry
   * 
   * @param key - Cache key cần xóa
   */
  remove(key: string): void {
    const fullKey = this.getFullKey(key);
    localStorage.removeItem(fullKey);
    this.updateStats();
    this.logDebug(`Cache removed: ${key}`);
  }
  
  /**
   * Xóa tất cả cache entries của LMS
   */
  clear(): void {
    const keys = this.getAllKeys();
    keys.forEach(key => localStorage.removeItem(key));
    this.stats = { hits: 0, misses: 0, size: 0, keys: [] };
    this.logDebug('All cache cleared');
  }
  
  /**
   * Invalidate cache và force refresh
   * Alias cho remove() nhưng semantic rõ ràng hơn
   * 
   * @param key - Cache key cần invalidate
   */
  invalidate(key: string): void {
    this.remove(key);
    this.logDebug(`Cache invalidated: ${key}`);
  }
  
  /**
   * Refresh cache trong background (không block UI)
   * Sử dụng requestIdleCallback để chạy khi browser idle
   * 
   * @param key - Cache key
   * @param fetchFunction - Async function để fetch data mới
   * @param ttl - Time-to-live cho cached data
   */
  refreshInBackground<T>(
    key: string,
    fetchFunction: () => Promise<T>,
    ttl: number
  ): void {
    const refresh = async () => {
      try {
        this.logDebug(`Background refresh started: ${key}`);
        const data = await fetchFunction();
        this.set(key, data, ttl);
        this.logDebug(`Background refresh completed: ${key}`);
      } catch (error) {
        console.error(`Background refresh failed for ${key}:`, error);
      }
    };
    
    // Dùng requestIdleCallback nếu có, fallback về setTimeout
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => refresh(), { timeout: 2000 });
    } else {
      setTimeout(() => refresh(), 1000);
    }
  }
  
  /**
   * Lấy cache statistics
   * 
   * @returns CacheStats object với hits, misses, size, keys
   */
  getStats(): CacheStats {
    this.updateStats();
    return { ...this.stats };
  }
  
  /**
   * Lấy hit ratio percentage
   * 
   * @returns Hit ratio từ 0-100
   */
  getHitRatio(): number {
    const total = this.stats.hits + this.stats.misses;
    if (total === 0) return 0;
    return (this.stats.hits / total) * 100;
  }
  
  // ==================== Private Helper Methods ====================
  
  /**
   * Tạo full key với prefix
   */
  private getFullKey(key: string): string {
    return `${this.prefix}${key}`;
  }
  
  /**
   * Lấy tất cả cache keys của LMS
   */
  private getAllKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        keys.push(key);
      }
    }
    return keys;
  }
  
  /**
   * Cập nhật cache statistics
   */
  private updateStats(): void {
    const keys = this.getAllKeys();
    this.stats.keys = keys.map(k => k.replace(this.prefix, ''));
    this.stats.size = this.getStorageSize();
  }
  
  /**
   * Tính tổng size của cache (bytes)
   */
  private getStorageSize(): number {
    let size = 0;
    const keys = this.getAllKeys();
    
    keys.forEach(key => {
      const item = localStorage.getItem(key);
      if (item) {
        size += item.length + key.length;
      }
    });
    
    return size;
  }
  
  /**
   * Xóa các entries cũ nhất khi storage đầy
   * Xóa 25% entries cũ nhất
   */
  private cleanOldEntries(): void {
    const keys = this.getAllKeys();
    const entries: Array<{ key: string; timestamp: number }> = [];
    
    // Collect all entries với timestamps
    keys.forEach(key => {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const cached: CachedData<any> = JSON.parse(item);
          entries.push({ key, timestamp: cached.timestamp });
        }
      } catch (error) {
        // Invalid entry, xóa luôn
        localStorage.removeItem(key);
      }
    });
    
    // Sort by timestamp (cũ nhất trước)
    entries.sort((a, b) => a.timestamp - b.timestamp);
    
    // Xóa 25% cũ nhất
    const removeCount = Math.ceil(entries.length * 0.25);
    for (let i = 0; i < removeCount; i++) {
      localStorage.removeItem(entries[i].key);
      this.logDebug(`Cleaned old entry: ${entries[i].key}`);
    }
    
    this.updateStats();
  }
  
  /**
   * Xóa tất cả expired entries
   */
  private cleanExpiredEntries(): void {
    const now = Date.now();
    const keys = this.getAllKeys();
    let cleanedCount = 0;
    
    keys.forEach(key => {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const cached: CachedData<any> = JSON.parse(item);
          
          // Skip entries với TTL vô hạn
          if (cached.expiresAt === Infinity) {
            return;
          }
          
          if (now > cached.expiresAt) {
            localStorage.removeItem(key);
            cleanedCount++;
            this.logDebug(`Expired entry removed: ${key}`);
          }
        }
      } catch (error) {
        // Invalid entry, xóa luôn
        localStorage.removeItem(key);
        cleanedCount++;
      }
    });
    
    if (cleanedCount > 0) {
      this.logDebug(`Cleaned ${cleanedCount} expired entries`);
      this.updateStats();
    }
  }
  
  /**
   * Initialize cleanup timer
   */
  private initCleanup(): void {
    // Cleanup expired entries định kỳ
    this.cleanupTimer = setInterval(() => {
      this.cleanExpiredEntries();
    }, GlobalCacheSettings.cleanupInterval);
    
    // Cleanup khi window unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.cleanExpiredEntries();
      });
    }
  }
  
  /**
   * Log debug message nếu debug mode enabled
   */
  private logDebug(message: string, data?: any): void {
    if (GlobalCacheSettings.enableDebug) {
      if (data !== undefined) {
        console.log(`[CacheService] ${message}`, data);
      } else {
        console.log(`[CacheService] ${message}`);
      }
    }
  }
  
  /**
   * Destroy service (for testing)
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }
}

// Export singleton instance
export const CacheService = new CacheServiceClass();